// ════════════════════════════════════════════════════════════════════
// push-platform.ts — runtime platform detection + prompt cooldown.
//
// Two consumers: PushPermissionPrompt (on /today) decides whether to
// render and which variant; PushNotificationsToggle (in Profile) hides
// itself on 'unsupported'. Both call detectPlatform().
//
// iOS Safari without the app installed as a PWA cannot receive push
// notifications at all (WebKit limitation through 2026) — those users
// see an "Add to Home Screen" hint instead of a real permission ask.
//
// All checks are SSR-safe: navigator/window/Notification are guarded.
// ════════════════════════════════════════════════════════════════════

const COOLDOWN_KEY = 'alma:push:prompted_at';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export type Platform = 'standard' | 'ios_safari_no_pwa' | 'unsupported';

export function detectPlatform(): Platform {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'unsupported';
  }
  if (
    !('serviceWorker' in navigator) ||
    typeof Notification === 'undefined' ||
    !('PushManager' in window)
  ) {
    return 'unsupported';
  }

  if (isIOS() && !isStandalonePWA()) {
    return 'ios_safari_no_pwa';
  }
  return 'standard';
}

// iPhone / iPod always reports those words in UA. iPad on iOS 13+ in
// "Request Desktop Website" (the default) reports a Mac UA — the only
// reliable disambiguation is navigator.maxTouchPoints, which is 0 on
// real Macs and >1 on iPads.
function isIOS(): boolean {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return true;
  return false;
}

// Installed-PWA detection. Both checks needed: matchMedia covers
// Chrome/Android/desktop standalone; navigator.standalone is Apple's
// legacy property that older iOS Safari still relies on.
function isStandalonePWA(): boolean {
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  const nav = navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) return true;
  return false;
}

// Whether to render the inline prompt on /today. Combines platform
// support, permission state, and the 7-day cooldown after dismissal.
export function shouldShowPrompt(): boolean {
  const platform = detectPlatform();
  if (platform === 'unsupported') return false;
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission !== 'default') return false;

  try {
    const raw = window.localStorage.getItem(COOLDOWN_KEY);
    if (!raw) return true;
    const stamped = Number.parseInt(raw, 10);
    if (!Number.isFinite(stamped)) return true;
    return Date.now() - stamped > COOLDOWN_MS;
  } catch {
    // localStorage blocked (private mode etc.) — fall through to showing.
    return true;
  }
}

export function markPromptDismissed(): void {
  try {
    window.localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
  } catch {
    /* swallow — worst case the prompt re-appears next session */
  }
}

export function clearPromptCooldown(): void {
  try {
    window.localStorage.removeItem(COOLDOWN_KEY);
  } catch {
    /* swallow */
  }
}
